import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { GetTurvReport, Me } from "./../../api/users";
import { PostGet } from "./../../api/post";
import { GetAcativeRestaurants, RestaurantGet } from "./../../api/restaurant";
import { TYLevel, DirectorLevel } from "../../constants";

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function getMonthStartDate() {
  const date = new Date();
  date.setDate(1);
  return formatDate(date);
}

function getYesterdayDate() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return formatDate(date);
}

const Employee = ({ token }) => {
  const [startDate, setStartDate] = useState(getMonthStartDate());
  const [endDate, setEndDate] = useState(getYesterdayDate());
  const [restaurantId, setRestaurantId] = useState(0);
  const [restaurantList, setRestaurantList] = useState([]);
  const [accessLevel, setAccessLevel] = useState(0);
  const [userRestaurantName, setUserRestaurantName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canPickRestaurant = accessLevel >= TYLevel;
  const isDirector = accessLevel >= DirectorLevel && accessLevel < TYLevel;

  useEffect(() => {
    async function init() {
      try {
        const user = await Me(token);
        const post = await PostGet(token, user.post_id);
        setAccessLevel(post.access_level);

        const restaurant = await RestaurantGet(token, user.restaurant_id);
        setUserRestaurantName(restaurant?.name || "");

        if (post.access_level >= TYLevel) {
          const restaurants = await GetAcativeRestaurants(token);
          setRestaurantList(restaurants || []);
        }
      } catch (err) {
        console.error(err);
        setError("Не удалось загрузить профиль");
      }
    }
    init();
  }, [token]);

  async function downloadReport() {
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }
    if (startDate > endDate) {
      setError("Дата начала не может быть позже даты окончания");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const selectedRestaurantId = canPickRestaurant ? restaurantId : 0;
      const response = await GetTurvReport(
        token,
        startDate,
        endDate,
        selectedRestaurantId,
      );

      if (!response?.data) {
        setError("Не удалось скачать отчёт");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "turv_report.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Ошибка при формировании отчёта");
    } finally {
      setLoading(false);
    }
  }

  function applyMonthToDatePreset() {
    setStartDate(getMonthStartDate());
    setEndDate(getYesterdayDate());
  }

  return (
    <div className="container turv-report-page">
      <div className="row mb-3">
        <div className="col-12">
          <Link to="/reports" className="text-decoration-none">
            ← К списку отчётов
          </Link>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card report-card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h3 mb-2">Табель учёта рабочего времени</h1>
              <p className="text-muted mb-4">
                Сравнение плановых смен, фактических отметок Boss Control и
                отклонений за выбранный период.
              </p>

              {isDirector && userRestaurantName && (
                <div className="alert alert-light border mb-4">
                  <strong>Ваш ресторан:</strong> {userRestaurantName}
                  <div className="small text-muted mt-1">
                    Отчёт формируется только по сотрудникам этого ресторана.
                  </div>
                </div>
              )}

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Начало периода</label>
                  <input
                    className="form-control"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Конец периода</label>
                  <input
                    className="form-control"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                {canPickRestaurant && (
                  <div className="col-12">
                    <label className="form-label">Ресторан</label>
                    <select
                      className="form-select"
                      value={restaurantId}
                      onChange={(e) =>
                        setRestaurantId(Number(e.target.value))
                      }
                    >
                      <option value={0}>Все рестораны</option>
                      {restaurantList.map((restaurant) => (
                        <option key={restaurant.id} value={restaurant.id}>
                          {restaurant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={applyMonthToDatePreset}
                    >
                      С 1-го числа по вчера
                    </button>
                  </div>
                </div>

                <div className="col-12">
                  <button
                    className="btn btn-primary w-100"
                    onClick={downloadReport}
                    disabled={loading}
                  >
                    {loading ? "Формирование..." : "Скачать Excel"}
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger mt-3 mb-0">{error}</div>
              )}

              <ul className="small text-muted mt-4 mb-0">
                <li>План — смены из расписания</li>
                <li>Факт — отметки Boss Control с пересчётом часов</li>
                <li>Отклонения — разница плана и факта</li>
                <li>Максимальный период — 31 день</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Employee;
